import type { CursorPosition } from './save-cursor';

import { editorsSelector, elementHasSelection } from './save-cursor';

const isElementInViewport = (element: HTMLElement) => {
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <=
      (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

const calculateScrollTopForVisibility = (editorElement: Element): number => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return editorElement.scrollTop;
  }

  const range = selection.getRangeAt(0);
  const rangeRect = range.getBoundingClientRect();
  const editorRect = editorElement.getBoundingClientRect();

  const isVisible =
    rangeRect.top >= editorRect.top && rangeRect.bottom <= editorRect.bottom;

  if (isVisible) {
    return editorElement.scrollTop;
  }

  const cursorTopRelativeToEditor = rangeRect.top - editorRect.top;
  return cursorTopRelativeToEditor + (cursorTopRelativeToEditor < 0 ? -50 : 50);
};

const restoreCursorPosition = (
  targetElement: HTMLElement,
  cursorPosition: number,
) => {
  const selection = window.getSelection();
  if (!selection) return;

  const range = document.createRange();
  const editorElement = targetElement.closest(editorsSelector) || targetElement;
  const walker = document.createTreeWalker(editorElement, NodeFilter.SHOW_TEXT);

  let currentPos = 0;
  let node = walker.nextNode();
  let targetNode = null;
  let targetOffset = 0;

  while (node) {
    const nodeLength = node.textContent?.length || 0;

    if (currentPos + nodeLength >= cursorPosition) {
      targetNode = node;
      targetOffset = cursorPosition - currentPos;
      break;
    }

    currentPos += nodeLength;
    node = walker.nextNode();
  }

  if (targetNode) {
    const maxOffset = targetNode.textContent?.length || 0;
    targetOffset = Math.min(targetOffset, maxOffset);

    range.setStart(targetNode, targetOffset);
    range.setEnd(targetNode, targetOffset);

    selection.removeAllRanges();
    selection.addRange(range);

    const calculatedScrollTop = calculateScrollTopForVisibility(editorElement);
    editorElement.scrollTo({
      top: calculatedScrollTop,
      behavior: 'smooth',
    });
  }
};

export const restoreCursor = ({
  element,
  fieldName,
  cursorPosition,
  selectionStart,
  selectionEnd,
}: CursorPosition) => {
  let targetElement: HTMLElement | null = element;
  if (!document.contains(element)) {
    const newElement = document.querySelector(
      `form [name="${fieldName}"],` +
        `[data-field-name="${fieldName}"] [contenteditable="true"],` +
        `[data-field-name="${fieldName}"] div.codex-editor__redactor`,
    );
    targetElement = newElement as HTMLElement | null;
  }

  if (!targetElement) return;

  setTimeout(() => {
    targetElement.focus();

    if (targetElement.closest(editorsSelector)) {
      if (cursorPosition) {
        restoreCursorPosition(targetElement, cursorPosition);
      }
    } else if (
      elementHasSelection(targetElement) &&
      typeof targetElement.selectionStart === 'number' &&
      typeof selectionStart === 'number' &&
      typeof selectionEnd === 'number'
    ) {
      targetElement.setSelectionRange(selectionStart, selectionEnd);
    }

    if (!isElementInViewport(targetElement)) {
      targetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, 10);
};
